import React, { useEffect, useRef, useState } from "react";

import useDebounce from "hooks/useDebounce";
import { Button } from "primereact/button";
import { CalendarProps } from "primereact/calendar";
import { T } from "services/lang/LanguageService";
import { DweenTimeUnitEnum, truncateDate } from "utils/time-utils";
import { ChronoUnit, Instant } from "@js-joda/core";
import { useTimezone } from "hooks/useTimezone";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useUserState } from "../../../store/user-store";

export type CalendarWrapperProps = {
  value: Date | undefined;
  onChange(date: Date): void;
  onInstantChange?(instant: Instant): void;
  dateFrequency?: DweenTimeUnitEnum;
  debounceTimeInMs?: number;
} & Omit<CalendarProps, "value" | "onChange">;

const CalendarWrapper = ({
  value,
  onChange,
  onInstantChange,
  dateFrequency,
  debounceTimeInMs,
  ...props
}: CalendarWrapperProps) => {
  const userLocale = useUserState(state => state.getLocaleDateFns());
  const tz = useTimezone();

  const valueRef = useRef<Date | null>(null);
  const [date, setDate] = useState<Date | null>(null);
  const debouncedValue = useDebounce(date, { delay: debounceTimeInMs });

  useEffect(() => {
    const handleEnterPressed = (event: KeyboardEvent) =>
      (event.code === "Enter" || event.code === "NumpadEnter") && replaceDateValue();

    window.addEventListener("keydown", handleEnterPressed);

    return () => window.removeEventListener("keydown", handleEnterPressed);
  }, []);

  useEffect(() => {
    debouncedValue && mainOnChange(debouncedValue);
  }, [debouncedValue]);

  useEffect(() => {
    valueRef.current && (valueRef.current = null);
  }, [value]);

  const mainOnChange = (newDate: Date) => {
    onChange(newDate);
    onInstantChange?.(newDate.toInstant());
  };

  const mainSetDate = (newDate: Date) => {
    setDate(newDate);
  };

  const replaceDateValue = () => {
    if (valueRef.current != null && value !== valueRef.current) {
      mainOnChange(valueRef.current);
    }
  };

  const onCalendarChange = (date: Date | null, event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
    if (!date) return;

    const pickedDate = truncateDate(date as Date, ChronoUnit.MINUTES, tz.company);

    if (event?.type === "input") {
      valueRef.current = pickedDate;
    } else {
      mainSetDate(pickedDate);
      mainOnChange(pickedDate);
    }
  };

  const truncateToCompany = (unit: ChronoUnit) => {
    if (!value) return;
    const truncatedDate = truncateDate(value, unit, tz.company);
    mainOnChange(truncatedDate);
  };

  const calendarFooterTemplate = () => {
    return (
      <div className="flex flex-row justify-content-between">
        <Button
          type="button"
          label={T("action.truncate-hours")}
          className="p-button-sm p-button-text p-button-outlined"
          onClick={() => truncateToCompany(ChronoUnit.DAYS)}
        />
        <Button
          type="button"
          label={T("action.truncate-minutes")}
          className="p-button-sm p-button-text p-button-outlined"
          onClick={() => truncateToCompany(ChronoUnit.HOURS)}
        />
      </div>
    );
  };

  // <Calendar
  //   value={value}
  //   onChange={onCalendarChange}
  //   footerTemplate={props.showTime === false ? undefined : calendarFooterTemplate}
  //   showIcon
  //   iconPos="left"
  //   dateFormat={DEFAULT_CALENDAR_FORMAT}
  //   showTime={props.showTime ?? true}
  //   onBlur={replaceDateValue}
  //   className={`p-inputtext-sm ${props.className || ""}`}
  //   {...props}
  // />

  return (
    <DatePicker
      selected={value}
      onChange={onCalendarChange}
      locale={userLocale.code}
      showTimeSelect
      className={`p-inputtext-sm ${props.className || ""}`}
      dateFormat="yyyy-MM-dd HH:mm"
    />
  );
};

export default CalendarWrapper;
